import type { CSSProperties, MouseEvent, ReactNode } from "react";

/**
 * TheCore Studio — Button.
 * Pill-forward action button. Terracotta primary by default.
 */
export type ButtonVariant = "primary" | "secondary" | "ghost" | "soft" | "danger";
export type ButtonSize = "sm" | "md" | "lg";
export type ButtonShape = "pill" | "rounded";

export interface ButtonProps {
  children?: ReactNode;
  /** Visual emphasis. @default 'primary' */
  variant?: ButtonVariant;
  /** @default 'md' */
  size?: ButtonSize;
  /** @default 'pill' */
  shape?: ButtonShape;
  fullWidth?: boolean;
  disabled?: boolean;
  /** Native tooltip — e.g. "why is this disabled" while offline. */
  title?: string;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  type?: "button" | "submit" | "reset";
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  style?: CSSProperties;
}

const SIZES: Record<ButtonSize, { height: number; padding: string; font: string; gap: number }> = {
  sm: { padding: "0 14px", height: 36, font: "var(--fw-semibold) var(--fs-sm)/1 var(--font-body)", gap: 6 },
  md: { padding: "0 20px", height: 44, font: "var(--fw-semibold) var(--fs-md)/1 var(--font-body)", gap: 8 },
  lg: { padding: "0 28px", height: 54, font: "var(--fw-bold) var(--fs-lg)/1 var(--font-body)", gap: 10 },
};

const VARIANTS: Record<ButtonVariant, CSSProperties> = {
  primary: {
    /* Resting background is terracotta-600 (#A85638) so cream button text
       hits WCAG AA >=4.5:1 at control-text sizes; --brand (#C06B47) stays
       the decorative primary for brand-soft chips and ghost text where it
       sits on a light surface and clears AA without darkening. */
    background: "var(--brand-hover)",
    color: "var(--text-on-brand)",
    border: "1px solid transparent",
    boxShadow: "var(--shadow-brand)",
  },
  secondary: {
    background: "var(--surface-card)",
    color: "var(--text-strong)",
    border: "1px solid var(--border-default)",
    boxShadow: "var(--shadow-xs)",
  },
  ghost: {
    background: "transparent",
    color: "var(--brand)",
    border: "1px solid transparent",
    boxShadow: "none",
  },
  soft: {
    background: "var(--brand-soft)",
    color: "var(--brand-press)",
    border: "1px solid transparent",
    boxShadow: "none",
  },
  danger: {
    background: "var(--rust-500)",
    color: "#fff",
    border: "1px solid transparent",
    boxShadow: "0 8px 22px rgba(181,70,46,0.28)",
  },
};

export function Button({
  children,
  variant = "primary",
  size = "md",
  shape = "pill",
  fullWidth = false,
  disabled = false,
  title,
  iconLeft = null,
  iconRight = null,
  type = "button",
  onClick,
  style = {},
  ...rest
}: ButtonProps) {
  const s = SIZES[size] ?? SIZES.md;
  const v = VARIANTS[variant] ?? VARIANTS.primary;

  return (
    <button
      type={type}
      disabled={disabled}
      title={title}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: s.gap,
        height: s.height,
        padding: s.padding,
        width: fullWidth ? "100%" : "auto",
        font: s.font,
        letterSpacing: "var(--ls-snug)",
        borderRadius: shape === "pill" ? "var(--radius-pill)" : "var(--radius-md)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        transition:
          "transform var(--dur-fast) var(--ease-out), filter var(--dur-fast) var(--ease-out), background var(--dur-fast) var(--ease-out)",
        whiteSpace: "nowrap",
        ...v,
        ...style,
      }}
      onMouseDown={(e) => {
        if (!disabled) e.currentTarget.style.transform = "scale(0.97)";
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = "scale(1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
        e.currentTarget.style.filter = "none";
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.filter = "brightness(0.96)";
      }}
      {...rest}
    >
      {iconLeft && <span style={{ display: "inline-flex" }}>{iconLeft}</span>}
      {children}
      {iconRight && <span style={{ display: "inline-flex" }}>{iconRight}</span>}
    </button>
  );
}
